/**
 * Call Reports — data fetching.
 *
 * All four call reports take the same window and return the same
 * `{ rows, total }` envelope, so they share one hook parameterised by path
 * rather than getting four near-identical copies (which is how the fifth
 * report ends up with subtly different error handling).
 *
 * Paging is client-side, matching the conversation reports: the window is
 * already bounded by the drawer, so the whole result set is fetched once and
 * the shell slices it, rather than a round trip per page for data the browser
 * already holds.
 */
import { useCallback, useState } from 'react'
import { api } from '../api/client.js'

/** The window every call report shares. */
export interface CallReportFilters {
  timezone: string
  interval: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  /** Report-specific filters, already flattened to query-string values. */
  extra: Record<string, string>
}

/**
 * Filters → query string.
 *
 * Empty `extra` values are dropped rather than sent blank: an empty
 * `queueIds=` is harmless but noisy, and it makes the exported URL misleading
 * about what was actually filtered.
 */
export function buildCallReportParams(filters: CallReportFilters): URLSearchParams {
  const params = new URLSearchParams({
    timezone: filters.timezone,
    interval: filters.interval,
    startDate: filters.startDate,
    endDate: filters.endDate,
    startTime: filters.startTime,
    endTime: filters.endTime,
  })
  for (const [key, value] of Object.entries(filters.extra)) {
    if (value !== '') params.set(key, value)
  }
  return params
}

interface UseCallReportResult {
  rows: Record<string, unknown>[]
  total: number
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: CallReportFilters) => Promise<void>
  reset: () => void
}

export function useCallReport(path: string): UseCallReportResult {
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(
    async (filters: CallReportFilters): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        const params = buildCallReportParams(filters)
        const res = await api.get<{ rows: Record<string, unknown>[]; total: number }>(
          `${path}?${params.toString()}`,
        )
        setRows(res.rows ?? [])
        setTotal(res.total ?? 0)
        setHasRun(true)
      } catch (err) {
        setRows([])
        setTotal(0)
        // hasRun stays true so the shell shows the error against an empty
        // table rather than falling back to the "no report yet" state, which
        // would read as though nothing had been asked for.
        setHasRun(true)
        setError(err instanceof Error ? err.message : 'Could not load the report.')
      } finally {
        setLoading(false)
      }
    },
    [path],
  )

  const reset = useCallback((): void => {
    setRows([])
    setTotal(0)
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, total, loading, error, hasRun, run, reset }
}
